import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Badge, Button, ErrorPanel, PageHeader, PanelCard, PillTabs, Spinner, TextLink } from '../../school_panel/components/ui';
import {
  useAuthoringExam, useConfirmWrittenMark, useExamMarking, useExamMarkingQuestion,
} from '../../school_panel/api/hooks';
import type { MarkingAnswer, MarkingStatus } from '../../school_panel/api/types';
import { useAuth } from '../../utils/Auth/AuthProvider';
import AnswerPhotoThumb from './components/AnswerPhotoThumb';
import AnswerPhotoViewer from './components/AnswerPhotoViewer';

const ALLOWED = new Set(['teacher', 'school_admin', 'school', 'admin']);

function saveError(err: unknown): string {
  const data = (err as { response?: { data?: { marks?: unknown; detail?: unknown } } })?.response?.data;
  const msg = data?.marks ?? data?.detail;
  if (typeof msg === 'string' && msg) return msg;
  if (Array.isArray(msg) && typeof msg[0] === 'string') return msg[0];
  return 'Could not save the mark. Try again.';
}

/**
 * Key events typed into a field, or Enter on a focused control, are not marking
 * shortcuts. A focused mark button is the exception: Enter there confirms the
 * mark (the handler prevents the button's own Enter activation).
 */
function ignoreKey(e: KeyboardEvent): boolean {
  if (e.ctrlKey || e.metaKey || e.altKey) return true;
  const t = e.target as HTMLElement | null;
  if (!t || typeof t.tagName !== 'string') return false;
  const tag = t.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select' || t.isContentEditable) return true;
  if (t.dataset?.markButton != null) return false;
  return e.key === 'Enter' && (tag === 'button' || tag === 'a');
}

function AiBox({ answer, max }: { answer: MarkingAnswer; max: number }) {
  if (answer.ai_status === 'pending') {
    return (
      <div className="flex items-center gap-2 rounded-[12px] bg-mist px-3 py-2 text-sm text-muted-2">
        <Spinner size="sm" /> <span>AI is suggesting a mark…</span>
      </div>
    );
  }
  if (answer.ai_status === 'done' && answer.ai_marks != null) {
    return (
      <div className="rounded-[12px] bg-mist px-3 py-2 text-sm">
        <p className="font-semibold text-ink">AI suggests {answer.ai_marks}/{max}</p>
        {answer.ai_reason && <p className="mt-1 text-muted-2">{answer.ai_reason}</p>}
      </div>
    );
  }
  // The server skips for the school limit and fails for an own-key error.
  const reason = answer.ai_status === 'skipped' || answer.ai_status === 'failed' ? answer.ai_skip_reason ?? '' : '';
  const reasonText = reason === 'ai_limit'
    ? 'School AI credit ended – mark by hand.'
    : reason.startsWith('openrouter_')
      ? "Your OpenRouter key couldn't mark this – mark by hand."
      : null;
  if (reasonText) {
    return (
      <div className="flex flex-wrap items-center gap-x-2 rounded-[12px] bg-surface-2 px-3 py-1 text-sm text-muted-2">
        <span>{reasonText}</span>
        <TextLink to="/settings/ai" className="text-ink underline underline-offset-[3px]">Get more AI credit</TextLink>
      </div>
    );
  }
  return <p className="rounded-[12px] bg-surface-2 px-3 py-2 text-sm text-muted-2">No AI suggestion — mark by hand</p>;
}

export default function MarkingPage() {
  const { token, role } = useAuth();
  const allowed = !!token && ALLOWED.has(role ?? '');
  const examId = Number(useParams().examId);
  if (!allowed) {
    return (
      <>
        <PageHeader title="Marking" trail={[{ label: 'Exams', to: '/exams/exam-list' }]} />
        <div role="alert" className="rounded-[12px] bg-danger-bg p-4 text-danger-ink">You are not authorised to mark this worksheet.</div>
      </>
    );
  }
  return <Marking examId={examId} />;
}

function Marking({ examId }: { examId: number }) {
  const exam = useAuthoringExam(examId);
  const overview = useExamMarking(examId);
  const [pickedQid, setPickedQid] = useState<number | null>(null);
  const [status, setStatus] = useState<MarkingStatus>('to_mark');
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [picked, setPicked] = useState<{ id: number; v: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // "qid:status" of a question/tab whose last answer was just confirmed.
  const [doneKey, setDoneKey] = useState<string | null>(null);
  // Index of the answer photo open in the viewer (null = closed).
  const [viewer, setViewer] = useState<number | null>(null);

  const questions = overview.data?.questions ?? [];
  const defaultQid = (questions.find((q) => q.to_mark > 0) ?? questions[0])?.question_id ?? null;
  const qid = pickedQid ?? defaultQid;
  // Pin the first selection so a refetched overview (counts change after every
  // confirm) never moves the teacher to another question.
  useEffect(() => {
    if (pickedQid == null && defaultQid != null) setPickedQid(defaultQid);
  }, [pickedQid, defaultQid]);
  const done = doneKey === `${qid}:${status}`;
  const detail = useExamMarkingQuestion(examId, qid, status);
  const confirm = useConfirmWrittenMark(examId);

  const answers = detail.data?.answers ?? [];
  const found = currentId == null ? -1 : answers.findIndex((a) => a.id === currentId);
  const index = found >= 0 ? found : 0;
  const answer: MarkingAnswer | undefined = done ? undefined : answers[index];
  // Credit uses the snapshot taken at submission, even if the question was edited since.
  const max = answer?.max_marks ?? detail.data?.question.max_marks ?? 0;
  const suggested = answer ? (answer.marks ?? (answer.ai_status === 'done' ? answer.ai_marks : null)) : null;
  const mark = answer && picked?.id === answer.id ? picked.v : suggested;

  const resetPosition = () => { setViewer(null); setCurrentId(null); setPicked(null); setError(null); setDoneKey(null); };
  const selectQuestion = (id: number) => { setPickedQid(id); resetPosition(); };
  const selectStatus = (s: MarkingStatus) => { setStatus(s); resetPosition(); };

  const choose = (v: number) => { if (answer) { setPicked({ id: answer.id, v }); setError(null); } };

  const skip = () => {
    if (answers.length < 2) return;
    setViewer(null);
    setCurrentId(answers[(index + 1) % answers.length].id);
    setError(null);
  };

  const submit = async () => {
    if (!answer || mark == null || confirm.isPending) return;
    setError(null);
    try {
      const res = await confirm.mutateAsync({ id: answer.id, marks: mark });
      setPicked(null);
      setViewer(null);
      if (status === 'to_mark') {
        if (res.next_id == null) setDoneKey(`${qid}:${status}`);
        else setCurrentId(res.next_id);
      } else {
        // The Marked list keeps the answer; move on to the next one when there is one.
        const next = answers[index + 1];
        if (next) setCurrentId(next.id);
      }
    } catch (err) {
      setError(saveError(err));
    }
  };

  // The listener reads the latest handlers through a ref so it is added once.
  const keys = useRef({ choose, submit, max, viewer });
  keys.current = { choose, submit, max, viewer };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (keys.current.viewer !== null) return;
      if (ignoreKey(e)) return;
      if (/^[0-9]$/.test(e.key)) {
        const v = Number(e.key);
        if (v <= keys.current.max) { e.preventDefault(); keys.current.choose(v); }
      } else if (e.key === 'Enter') {
        e.preventDefault();
        void keys.current.submit();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const title = exam.data?.name;
  const header = (
    <PageHeader
      title="Marking"
      subtitle={title}
      trail={[{ label: 'Exams', to: '/exams/exam-list' }]}
      actions={<Link to={`/exams/exam-detail/${examId}`} className="text-sm font-semibold text-ink underline underline-offset-[3px] hover:text-muted-2">Back to exam</Link>}
    />
  );

  if (overview.status === 'pending') return <>{header}<div className="flex justify-center p-10"><Spinner size="md" /></div></>;
  if (overview.status === 'error') {
    return <>{header}<ErrorPanel title="Marking could not be loaded" body="Try refreshing the page." onRetry={() => overview.refetch()} /></>;
  }
  if (questions.length === 0) {
    return <>{header}<PanelCard><p className="text-sm text-muted">This worksheet has no written questions.</p></PanelCard></>;
  }

  // The next question (in order, wrapping) that still has answers to mark.
  const at = questions.findIndex((row) => row.question_id === qid);
  const nextQuestion = [...questions.slice(at + 1), ...questions.slice(0, Math.max(at, 0))]
    .find((row) => row.question_id !== qid && row.to_mark > 0);
  const q = detail.data?.question;

  return (
    <div className="space-y-6">
      {header}
      <div className="grid gap-[18px] lg:grid-cols-[minmax(0,280px)_minmax(0,1fr)]">
        <PanelCard title="Questions" size="compact">
          <ul aria-label="Written questions" className="space-y-2">
            {questions.map((row) => {
              const active = row.question_id === qid;
              return (
                <li key={row.question_id}>
                  <button type="button" aria-current={active ? 'true' : undefined} onClick={() => selectQuestion(row.question_id)}
                    className={`flex w-full flex-col items-start gap-1.5 rounded-[12px] border px-3 py-2 text-left text-sm transition ${
                      active ? 'border-navy bg-mist text-ink' : 'border-line bg-surface text-ink hover:bg-surface-2'}`}>
                    <span className="line-clamp-2 font-medium">Q{row.n} · {row.text_excerpt}</span>
                    <Badge tone={row.to_mark > 0 ? 'warning' : 'success'}>{row.to_mark} to mark / {row.to_mark + row.marked}</Badge>
                  </button>
                </li>
              );
            })}
          </ul>
        </PanelCard>

        <div className="min-w-0 space-y-[18px]">
          <PillTabs<MarkingStatus> label="Answers to show" value={status} onChange={selectStatus}
            options={[{ key: 'to_mark', label: 'To mark' }, { key: 'marked', label: 'Marked' }]} />

          {detail.status === 'pending' ? (
            <div className="flex justify-center p-10"><Spinner size="md" /></div>
          ) : detail.status === 'error' || !q ? (
            <ErrorPanel title="Answers could not be loaded" body="Try refreshing the page." onRetry={() => detail.refetch()} />
          ) : (
            <>
              <PanelCard title="Question" size="compact">
                <p className="whitespace-pre-wrap text-sm text-ink">{q.text}</p>
                <div className="mt-3 rounded-[12px] bg-surface-2 px-3 py-2 text-sm">
                  <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Model answer</p>
                  <p className="mt-1 whitespace-pre-wrap text-ink">{q.model_answer}</p>
                  {q.notes && (
                    <>
                      <p className="mt-2 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Marking notes</p>
                      <p className="mt-1 whitespace-pre-wrap text-ink">{q.notes}</p>
                    </>
                  )}
                </div>
                <p className="mt-2 text-sm font-semibold text-muted-2">Out of {answer ? max : q.max_marks}</p>
              </PanelCard>

              {!answer ? (
                <PanelCard size="compact">
                  <p className="text-sm font-semibold text-ink">
                    {status === 'to_mark' ? 'All written answers marked' : 'No marked answers yet'}
                  </p>
                  {status === 'to_mark' && nextQuestion && (
                    <Button className="mt-3" size="sm" onClick={() => selectQuestion(nextQuestion.question_id)}>
                      {`Next question · Q${nextQuestion.n}`}
                    </Button>
                  )}
                </PanelCard>
              ) : (
                <PanelCard size="compact" title={answer.student.name}
                  actions={<span className="font-mono text-xs text-muted">{index + 1} of {answers.length}</span>}>
                  {(() => {
                    const photos = answer.photos ?? [];
                    return (
                      <>
                        <p className="whitespace-pre-wrap rounded-[12px] border border-line px-3 py-2 text-sm text-ink">
                          {answer.text === '' && photos.length > 0 ? 'Photo answer' : answer.text}
                        </p>
                        {photos.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {photos.map((ph, i) => (
                              <AnswerPhotoThumb key={ph.id} url={ph.url} index={i} total={photos.length} onOpen={() => setViewer(i)} />
                            ))}
                          </div>
                        )}
                        {viewer !== null && photos.length > 0 && (
                          <AnswerPhotoViewer photos={photos} index={Math.min(viewer, photos.length - 1)}
                            onIndex={setViewer} onClose={() => setViewer(null)} />
                        )}
                      </>
                    );
                  })()}
                  <div className="mt-3"><AiBox answer={answer} max={max} /></div>
                  {answer.marks != null && (
                    <p className="mt-2 text-sm text-muted-2">Marked {answer.marks}/{max}{answer.marked_by ? ` by ${answer.marked_by}` : ''}</p>
                  )}
                  <div role="group" aria-label="Mark" className="mt-4 flex flex-wrap gap-2">
                    {Array.from({ length: max + 1 }, (_, v) => (
                      <Button key={v} size="sm" variant={mark === v ? 'secondary' : 'ghost'} aria-pressed={mark === v}
                        aria-label={`Mark ${v}`} data-mark-button="" onClick={() => choose(v)} className="min-w-[44px]">
                        {v}
                      </Button>
                    ))}
                  </div>
                  {error && <p role="alert" className="mt-3 rounded-[12px] bg-danger-bg px-3 py-2 text-sm text-danger-ink">{error}</p>}
                  <div className="mt-4 flex flex-wrap items-center gap-2.5">
                    <Button onClick={() => void submit()} disabled={mark == null} loading={confirm.isPending}>Confirm &amp; next</Button>
                    <Button variant="ghost" onClick={skip} disabled={answers.length < 2}>Skip</Button>
                    <span className="text-xs text-muted">Keys: 0–{Math.min(max, 9)} pick a mark · Enter confirms</span>
                  </div>
                </PanelCard>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
